import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { MdArrowBack as ArrowBackIcon } from 'react-icons/md';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpIcon from '@mui/icons-material/Help';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useGetAttempt, useGetUserAttempts, type TQuizAnswerWithQuestion } from '@onesource/services';

/**
 * Format seconds to MM:SS or HH:MM:SS format
 */
const formatTime = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Answer Review Card Component
 * Shows question, user's answer, and correct answer (if allowed)
 */
const AnswerReviewCard = ({
  answer,
  questionNumber,
  showCorrectAnswers,
}: {
  answer: TQuizAnswerWithQuestion;
  questionNumber: number;
  showCorrectAnswers: boolean;
}) => {
  const { question } = answer;
  const options = question.options || [];

  // Get selected option labels
  const getSelectedOptions = () => {
    if (answer.selectedOptionIds && answer.selectedOptionIds.length > 0) {
      return options.filter(opt => answer.selectedOptionIds.includes(opt.id)).map(opt => opt.optionText);
    }
    return [];
  };

  // Get correct option labels
  const getCorrectOptions = () => {
    return options.filter(opt => opt.isCorrect).map(opt => opt.optionText);
  };

  // Get ordering display
  const getOrderingDisplay = (sequence: string[]) => {
    return sequence.map(id => options.find(opt => opt.id === id)?.optionText || 'Unknown').join(' → ');
  };

  // Determine icon based on correctness
  const getStatusIcon = () => {
    if (answer.isCorrect === null) {
      return <HelpIcon color="warning" />;
    }
    return answer.isCorrect ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />;
  };

  const getStatusColor = () => {
    if (answer.isCorrect === null) {
      return 'warning.main';
    }
    return answer.isCorrect ? 'success.main' : 'error.main';
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderLeft: 4,
        borderLeftColor: getStatusColor(),
      }}
    >
      {/* Question Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Question {questionNumber}
            </Typography>
            <Chip label={question.questionType.replace('_', ' ')} size="small" variant="outlined" />
            <Chip
              label={`${answer.pointsAwarded}/${question.points} pts`}
              size="small"
              color={answer.pointsAwarded === question.points ? 'success' : 'default'}
            />
          </Box>
          <Typography variant="body1" fontWeight={500}>
            {question.questionText}
          </Typography>
        </Box>
        <Box>{getStatusIcon()}</Box>
      </Box>

      {/* Answer Details */}
      <Box sx={{ pl: 2 }}>
        {/* User's Answer */}
        <Box mb={1}>
          <Typography variant="body2" color="text.secondary" component="span">
            Your answer:{' '}
          </Typography>
          <Typography variant="body2" component="span" fontWeight={500}>
            {question.questionType === 'short_answer' && (answer.textAnswer || '(No answer)')}
            {question.questionType === 'ordering' &&
              answer.orderingSequence &&
              (getOrderingDisplay(answer.orderingSequence) || '(No answer)')}
            {['multiple_choice', 'multiple_select', 'true_false'].includes(question.questionType) &&
              (getSelectedOptions().join(', ') || '(No answer)')}
          </Typography>
        </Box>

        {/* Correct Answer (if allowed and not short_answer) */}
        {showCorrectAnswers && question.questionType !== 'short_answer' && (
          <Box mb={1}>
            <Typography variant="body2" color="text.secondary" component="span">
              Correct answer:{' '}
            </Typography>
            <Typography variant="body2" component="span" color="success.main" fontWeight={500}>
              {question.questionType === 'ordering' ? (
                <>
                  {options
                    .sort((a, b) => a.position - b.position)
                    .map(opt => opt.optionText)
                    .join(' → ')}
                </>
              ) : (
                <>{getCorrectOptions().join(', ')}</>
              )}
            </Typography>
          </Box>
        )}

        {/* Explanation */}
        {showCorrectAnswers && question.explanation && (
          <Box
            mt={2}
            p={1.5}
            sx={{
              backgroundColor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>Explanation:</strong> {question.explanation}
            </Typography>
          </Box>
        )}

        {/* Manual Grading Feedback */}
        {answer.feedback && (
          <Box
            mt={2}
            p={1.5}
            sx={{
              backgroundColor: 'info.light',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">
              <strong>Feedback:</strong> {answer.feedback}
            </Typography>
          </Box>
        )}

        {/* Pending Grading Notice */}
        {answer.isCorrect === null && question.questionType === 'short_answer' && (
          <Alert severity="info" sx={{ mt: 1 }}>
            This answer requires manual grading and is pending review.
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

/**
 * Quiz Result Page
 * Full results view with score and answer review
 */
export const QuizResultPage = () => {
  const navigate = useNavigate();
  const { id: attemptId } = useParams({ from: '/_protected/community-hub/attempts/$id' });

  // Fetch attempt data
  const { data: attempt, isLoading, error } = useGetAttempt(attemptId);

  // Fetch user attempts for this quiz to check if retry is allowed
  const { data: userAttempts } = useGetUserAttempts(attempt?.quizId || '');

  // Calculate if retry is allowed
  const attemptsUsed = userAttempts?.length || 0;
  const maxAttempts = attempt?.quiz?.maxAttempts;
  const canRetry = !maxAttempts || attemptsUsed < maxAttempts;

  // Handle retry
  const handleRetry = () => {
    if (attempt?.quizId) {
      navigate({ to: '/community-hub/quizzes/$id', params: { id: attempt.quizId } });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load results: {error.message}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: '/community-hub' })} sx={{ mt: 2 }}>
          Back to Community Hub
        </Button>
      </Box>
    );
  }

  // No attempt found
  if (!attempt) {
    return (
      <Box p={3}>
        <Alert severity="warning">Attempt not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: '/community-hub' })} sx={{ mt: 2 }}>
          Back to Community Hub
        </Button>
      </Box>
    );
  }

  const showCorrectAnswers = attempt.quiz?.showCorrectAnswers ?? true;
  const sortedAnswers = [...(attempt.answers || [])].sort((a, b) => a.question.position - b.question.position);

  return (
    <Box p={3} maxWidth="900px" mx="auto">
      {/* Results Header Card */}
      <Card
        sx={{
          mb: 4,
          background: attempt.passed
            ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
            : attempt.passed === false
              ? 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)'
              : 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          {/* Pass/Fail Icon */}
          <Box mb={2}>
            {attempt.passed ? (
              <EmojiEventsIcon sx={{ fontSize: 64, color: 'success.main' }} />
            ) : attempt.passed === false ? (
              <CancelIcon sx={{ fontSize: 64, color: 'error.main' }} />
            ) : (
              <HelpIcon sx={{ fontSize: 64, color: 'warning.main' }} />
            )}
          </Box>

          {/* Pass/Fail Message */}
          <Typography variant="h4" gutterBottom>
            {attempt.passed
              ? 'Congratulations! You Passed!'
              : attempt.passed === false
                ? 'Not Quite - Keep Practicing!'
                : 'Results Pending'}
          </Typography>

          {/* Quiz Title */}
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {attempt.quiz?.title}
          </Typography>

          {/* Score */}
          <Box display="flex" justifyContent="center" gap={4} mt={3}>
            <Box>
              <Typography variant="h3" fontWeight="bold">
                {attempt.scorePercent !== null ? `${Math.round(attempt.scorePercent)}%` : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Score
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box>
              <Typography variant="h3" fontWeight="bold">
                {attempt.earnedPoints ?? '-'}/{attempt.totalPoints ?? '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Points
              </Typography>
            </Box>

            {attempt.timeSpentSeconds && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <AccessTimeIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h4" fontWeight="bold">
                      {formatTime(attempt.timeSpentSeconds)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Time Spent
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Status Badges */}
          <Box display="flex" justifyContent="center" gap={1} mt={3}>
            <Chip
              label={`Attempt ${attempt.attemptNumber}${maxAttempts ? ` of ${maxAttempts}` : ''}`}
              variant="outlined"
            />
            <Chip
              label={attempt.status.charAt(0).toUpperCase() + attempt.status.slice(1)}
              color={attempt.status === 'graded' ? 'success' : attempt.status === 'submitted' ? 'warning' : 'default'}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Answer Review Section */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom>
          Answer Review
        </Typography>
        {!showCorrectAnswers && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Correct answers are not shown for this quiz.
          </Alert>
        )}
        <Box display="flex" flexDirection="column" gap={2}>
          {sortedAnswers.map((answer, index) => (
            <AnswerReviewCard
              key={answer.id}
              answer={answer}
              questionNumber={index + 1}
              showCorrectAnswers={showCorrectAnswers}
            />
          ))}
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: '/community-hub' })}>
          Back to Community Hub
        </Button>

        {canRetry && (
          <Button variant="contained" startIcon={<ReplayIcon />} onClick={handleRetry}>
            Try Again
          </Button>
        )}
      </Box>

      {/* Max Attempts Notice */}
      {!canRetry && (
        <Alert severity="info" sx={{ mt: 3 }}>
          You have used all {maxAttempts} attempts for this quiz.
        </Alert>
      )}
    </Box>
  );
};
